import {useEffect, useMemo} from 'react';

import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {
  ContainerSchemaQuery,
  ContainerSchemaQueryVariables,
} from 'samples/plate-maps/__generated/ContainerSchemaQuery';
import {
  LiveSchematizedDisplayLayerMetadata,
  OutdatedSchematizedDisplayLayerMetadata,
  SchematizedDisplayLayerMetadata,
} from 'samples/plate-maps/PlateMapConstants';
import {CONTAINER_SCHEMA_QUERY} from 'samples/plate-maps/PlateMapQueryUtil';
import {getWellSchemaDisplayLayerOptions} from 'samples/plate-maps/PlateMapUtil';

function getAllDisplaySchemaLayers(
  queryResult: SkippableBenchlingQueryResult<ContainerSchemaQuery, ContainerSchemaQueryVariables>,
  initialOptions: Array<SchematizedDisplayLayerMetadata>
): Array<SchematizedDisplayLayerMetadata> | null {
  let liveOptions: Array<LiveSchematizedDisplayLayerMetadata> = [];
  switch (queryResult.type) {
    case QueryResultType.SUCCESS:
      liveOptions = getWellSchemaDisplayLayerOptions(queryResult.data);
      break;
    case QueryResultType.SKIPPED:
      liveOptions = [];
      break;
    case QueryResultType.PENDING:
    case QueryResultType.ERROR:
    default:
      return null;
  }
  const liveOptionKeys = new Set(liveOptions.map(({attribute}) => attribute.key));
  const uniqueInitialOptions: Array<OutdatedSchematizedDisplayLayerMetadata> = initialOptions
    .filter(({attribute}) => !liveOptionKeys.has(attribute.key))
    .map((layer) => ({...layer, outdated: true}));
  return [...uniqueInitialOptions, ...liveOptions.map((layer) => ({...layer, outdated: false}))];
}

/**
 * A hook to query for a well schema and transform its schema fields into display layers.
 * @param containerSchemaId API ID of the schema to load
 * @param onLoadSchemaFields A function to be called once after the schema fields are loaded. Accepts schema
 *                           field API Ids. Intended usage is to set e.g. initial schema field selection state
 * @returns A list of schema field display layer metadata, or null if the query is in progress, or empty array
 *          if no container schema id is given.
 */
export default function useWellSchemaLayers(
  containerSchemaId: string | null,
  onLoadSchemaFields: (schemaFields: Array<SchematizedDisplayLayerMetadata>) => void,
  initialWellSchemaOptions: Array<SchematizedDisplayLayerMetadata>
): Array<SchematizedDisplayLayerMetadata> | null {
  const containerSchemaQueryResult = useSkippableBenchlingQuery<
    ContainerSchemaQuery,
    ContainerSchemaQueryVariables
  >(CONTAINER_SCHEMA_QUERY, {
    variables: {storableSchemaId: containerSchemaId!},
    skip: containerSchemaId == null,
  });

  const wellSchemaLayers = useMemo(
    () => getAllDisplaySchemaLayers(containerSchemaQueryResult, initialWellSchemaOptions),
    // useBenchlingQuery helpfully creates a new object for the query result each time the hook fires. The
    // data shouldn't change after the result type does, so depend only on that to avoid infinite rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerSchemaQueryResult.type, initialWellSchemaOptions]
  );

  useEffect(() => {
    if (containerSchemaQueryResult.type === QueryResultType.SUCCESS) {
      onLoadSchemaFields(wellSchemaLayers!);
    }
    // There's no way to force consumers to useCallback onLoadSchemaFields, so don't depend on that either
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerSchemaQueryResult.type]);

  return wellSchemaLayers;
}
